import {useFlux, useFluxListener} from '@nlabs/arkhamjs-utils-react';
import {Button, PasswordStrengthMeter} from '@nlabs/gothamjs/components';
import {ArrowRight, CircleAlert, Clapperboard} from '@nlabs/gothamjs/icons';
import {Link} from '@nlabs/gothamjs/router';
import {AuthView} from '@nlabs/gothamjs/views';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {AUTH_EVENTS, createAuthActions} from '../../state/authActions.js';
import {primaryButton} from '../../theme/classes.js';
import {authLink} from './authClasses.js';
import {AUTH_CONTENT, validateAuthForm} from './authContent.js';
import {AuthFeedback} from './AuthFeedback.js';
import {AuthField} from './AuthField.js';

import type {FormEvent, ReactElement} from 'react';
import type {AuthEvent, AuthMode, AuthResult, AuthUser} from '../../state/authActions.js';

export interface AuthPageProps {
  readonly email?: string;
  readonly mode: AuthMode;
  readonly onSignedIn?: (user: AuthUser) => void;
  readonly token?: string;
}

const filmmakerHero = new URL('../../assets/filmmaker-hero.png', import.meta.url).href;

export const AuthPage = ({email: initialEmail = '', mode, onSignedIn, token: initialToken = ''}: AuthPageProps): ReactElement => {
  const flux = useFlux();
  const actions = useMemo(() => createAuthActions(flux), [flux]);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [marketingOptOut, setMarketingOptOut] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState(initialToken);
  const [error, setError] = useState('');
  const [result, setResult] = useState<AuthResult>();
  const focusName = useCallback((input: HTMLInputElement | HTMLTextAreaElement | null): void => {
    input?.focus({preventScroll: true});
  }, []);
  const content = AUTH_CONTENT[mode];
  const needsEmail = ['sign-in', 'sign-up', 'forgot-password', 'resend-verification'].includes(mode);
  const needsPassword = ['sign-in', 'sign-up', 'reset-password'].includes(mode);
  const isNewPassword = mode === 'sign-up' || mode === 'reset-password';
  const needsToken = mode === 'verify-email' || mode === 'reset-password';

  useEffect(() => {
    setEmail(initialEmail);
    setToken(initialToken);
    setPassword('');
    setConfirmPassword('');
    setError('');
    setResult(undefined);
    setBusy(false);
  }, [initialEmail, initialToken, mode]);

  useFluxListener(AUTH_EVENTS.PENDING, useCallback((event?: AuthEvent) => {
    if(event?.operation === mode) {
      setBusy(true);
      setError('');
    }
  }, [mode]));

  useFluxListener(AUTH_EVENTS.CHANGED, useCallback((event?: AuthEvent) => {
    if(event?.operation !== mode || !event.result) {
      return;
    }
    setBusy(false);
    setResult(event.result);
    setPassword('');
    setConfirmPassword('');
    if(mode === 'sign-in' && event.result.user) {
      onSignedIn?.(event.result.user);
    }
  }, [mode, onSignedIn]));

  useFluxListener(AUTH_EVENTS.ERROR, useCallback((event?: AuthEvent) => {
    if(event?.operation === mode) {
      setBusy(false);
      setError(flux.getState('auth.error', '') as string);
    }
  }, [flux, mode]));

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if(busy) {
      return;
    }
    const message = validateAuthForm(mode, {confirmPassword, email, name, password, token});
    setError(message);
    if(message) {
      return;
    }
    switch(mode) {
      case 'sign-in':
        void actions.signIn({email, password});
        break;
      case 'sign-up':
        void actions.signUp({email, marketingEmails: !marketingOptOut, name, password});
        break;
      case 'verify-email':
        void actions.verifyEmail(token);
        break;
      case 'resend-verification':
        void actions.resendVerification(email);
        break;
      case 'forgot-password':
        void actions.forgotPassword(email);
        break;
      case 'reset-password':
        void actions.resetPassword({password, token: token.trim()});
        break;
      case 'sign-out':
        void actions.signOut();
        break;
    }
  };

  return (
    <AuthView
      cardClassName="max-w-xl! rounded-[2rem]! border! border-white/40! bg-linear-to-br! from-[#d9cff8]! via-[#b9b6ee]! to-[#72c9ed]! p-6! text-slate-950! shadow-none! sm:p-9!"
      cardDescription={null}
      cardHeaderClassName="hidden!"
      cardTitle={null}
      className={`mx-auto! min-h-0! min-w-0! max-w-7xl! bg-transparent! px-5! py-6! text-white! sm:px-8! sm:py-10! [&>div]:items-start! [&>div]:gap-8! lg:[&>div]:gap-16! max-lg:[&>div]:grid-cols-1! ${mode === 'sign-up' ? '' : 'max-lg:[&_[data-slot=auth-view-hero]]:hidden!'}`}
      description={(
        <>
          <span className="mt-4 mb-6 hidden font-heading lg:block text-5xl leading-[1.05] font-normal tracking-tight text-white lg:text-6xl">
            Your next<br /><span className="text-sky-200">great scene.</span>
          </span>
          <span className="hidden max-w-md text-base leading-7 text-white/55 lg:block">
            From the first spark to the final frame. Give your creative process a place to unfold.
          </span>
          {mode === 'sign-up' && <span className="mt-6 block max-w-md space-y-4 text-sm leading-7 text-zinc-200"><span className="block">Spend your first month building projects, characters, and story plans for free. After that, keep planning for free or explore Starter, Creator, and Studio. No automatic upgrade or charge.</span><span className="block">Image generation, video generation, and video storage require a paid plan. Review available upgrades in your account after your first month.</span></span>}
          <span className="relative mt-8 hidden overflow-hidden rounded-[2rem] border border-white/10 lg:block">
            <img alt="A filmmaker working beside a cinema camera" className="h-64 w-full object-cover object-center" src={filmmakerHero} />
            <span className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-linear-to-t from-black/80 to-transparent px-5 pt-12 pb-4 text-xs text-white/70">
              <span className="flex items-center gap-2"><Clapperboard aria-hidden="true" size={16} />Made for making.</span>
              <span>Illustrative artwork</span>
            </span>
          </span>
        </>
      )}
      descriptionClassName="max-w-lg!"
      eyebrow="Create a new account"
      eyebrowClassName="text-xs! font-medium! text-violet-300!"
      title={null}
      titleClassName="hidden!">
      <header className="mb-6 border-b border-slate-950/10 pb-5">
        <p className="mb-3 text-xs font-semibold text-violet-950/65">Your Reactorbox account</p>
        <h1 className="text-3xl leading-tight font-normal tracking-tight text-slate-950">{content.title}</h1>
        <p className="mt-3 text-sm leading-6 text-slate-700">{content.description}</p>
      </header>
      {result ? <AuthFeedback mode={mode} result={result} /> : (
        <form aria-busy={busy} className="space-y-5" noValidate onSubmit={submit}>
          {mode === 'sign-up' && (
            <AuthField autoComplete="name" disabled={busy} id="auth-name" label="Name" maxLength={100} name="name" onChange={(event) => setName(event.target.value)} ref={focusName} required value={name} />
          )}
          {needsEmail && (
            <AuthField autoCapitalize="none" autoComplete="email" disabled={busy} id="auth-email" label="Email" maxLength={254} name="email" onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" required spellCheck={false} type="email" value={email} />
          )}
          {needsToken && (
            <div>
              <AuthField aria-describedby="auth-token-help" autoCapitalize="none" autoComplete="off" disabled={busy} id="auth-token" label={mode === 'verify-email' ? 'Verification token' : 'Reset token'} maxLength={512} name="token" onChange={(event) => setToken(event.target.value)} required spellCheck={false} value={token} />
              <p className="mt-2 text-xs leading-5 text-slate-700" id="auth-token-help">{initialToken ? 'Loaded from your link.' : 'Open the link from your email to fill this automatically.'}</p>
            </div>
          )}
          {needsPassword && (
            <div>
              <AuthField aria-describedby={isNewPassword ? 'auth-password-help' : undefined} autoComplete={isNewPassword ? 'new-password' : 'current-password'} disabled={busy} id="auth-password" label={mode === 'reset-password' ? 'New password' : 'Password'} name="password" onChange={(event) => setPassword(event.target.value)} required type="password" value={password} />
              {isNewPassword && <div className="mt-3" id="auth-password-help"><PasswordStrengthMeter className="[&_p]:text-slate-700! [&_strong]:text-slate-950!" password={password} /><p className="mt-2 text-xs leading-5 text-slate-700">Strength is a guide, not a requirement.</p></div>}
            </div>
          )}
          {isNewPassword && (
            <AuthField autoComplete="new-password" disabled={busy} id="auth-confirm-password" label="Confirm password" name="confirmPassword" onChange={(event) => setConfirmPassword(event.target.value)} required type="password" value={confirmPassword} />
          )}
          {mode === 'sign-in' && <Link className={`${authLink} -mt-2`} to="/forgot-password">Forgot password?</Link>}
          {error && (
            <div className="flex items-start gap-3 rounded-2xl border border-rose-700/20 bg-white/35 p-4 text-sm leading-6 text-rose-950" role="alert">
              <CircleAlert aria-hidden="true" className="mt-1 shrink-0" size={17} /><span>{error}</span>
            </div>
          )}
          {mode === 'sign-up' && <div className="space-y-3 text-sm text-slate-700"><p>We’ll add you to our mailing list for Reactorbox news and updates unless you opt out.</p><label className="flex cursor-pointer items-start gap-2.5 leading-6"><input checked={marketingOptOut} className="m-0 mt-1 size-4 shrink-0 cursor-pointer accent-violet-600 [color-scheme:light] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:cursor-not-allowed" disabled={busy} name="marketingOptOut" onChange={(event) => setMarketingOptOut(event.target.checked)} type="checkbox" /><span>Do not send me marketing emails.</span></label></div>}
          <Button className={`${primaryButton} w-full`} disabled={busy} icon={<ArrowRight aria-hidden="true" size={17} />} isLoading={busy} type="submit">{content.submit}</Button>
        </form>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-x-5 border-t border-slate-950/10 pt-3">
        {mode === 'sign-in' ? <Link className={authLink} to="/sign-up">Create an account</Link> : <Link className={authLink} to="/sign-in">Back to sign in</Link>}
        {['sign-in', 'sign-up', 'verify-email'].includes(mode) && <Link className={authLink} to="/resend-verification">Resend verification</Link>}
        {mode === 'reset-password' && <Link className={authLink} to="/forgot-password">Request a new reset link</Link>}
        {mode === 'sign-out' && !result && <Link className={authLink} to="/studio">Stay in the studio</Link>}
      </div>
    </AuthView>
  );
};
